import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { DeleteDialog } from "components";
import { ButtonPrimary, ButtonWarning } from "components/Atoms/Button";
import { toastSuccess } from "components/Toast/Toast";
import { extractorSessionListQueryFilter } from "extractorSession/list/useExtractorSessionCacheSync";
import React, { useContext } from "react";
import { getApiKeyForSidePanel } from "../DetailSidePanel";
import { SidePanelActionsContext } from "../SidePanelContext";
import { flightFromAiSidePanelApiKey } from "./flightFromAiSidePanelConstants";

interface FlightFromAiActionButtonProps {
  extractedFlightId: number
  extractorSessionId: number
  replaceFlightFromAiSidePanel: (extractedFlightId: number) => void
}

const getNextReadyForApprovalFlightId = async (
  extractorSessionId: number,
  extractedFlightId: number
) => {
  const response = await window.CoreApi.Extractor.GetExtractorSession(extractorSessionId, { toastOnError: true });
  const session = response.data.data;
  if (response.data.hasErrors || !session) return;

  const currentIndex = session.flights.findIndex(f => f.extractedFlightId === extractedFlightId);
  const flightsInReviewOrder = currentIndex === -1
    ? session.flights
    : [
      ...session.flights.slice(currentIndex + 1),
      ...session.flights.slice(0, currentIndex)
    ];

  return flightsInReviewOrder.find(f =>
    f.extractedFlightId !== extractedFlightId &&
    f.lifeCycle === "readyForApproval"
  )?.extractedFlightId;
};

const invalidateFlightFromAiActionQueries = async (
  queryClient: QueryClient,
  extractedFlightId: number
) => {
  await queryClient.invalidateQueries({
    queryKey: getApiKeyForSidePanel(flightFromAiSidePanelApiKey, extractedFlightId),
    refetchType: "none"
  });

  await queryClient.invalidateQueries({
    ...extractorSessionListQueryFilter,
    type: "active",
    refetchType: "active"
  });
};

export const DeleteButton = ({
  extractedFlightId,
  extractorSessionId,
  replaceFlightFromAiSidePanel
}: FlightFromAiActionButtonProps) => {
  const queryClient = useQueryClient();
  const { closePanel } = useContext(SidePanelActionsContext);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const nextExtractedFlightId = await getNextReadyForApprovalFlightId(extractorSessionId, extractedFlightId);
      const response = await window.CoreApi.Extractor.DeleteExtractedFlight({ extractedFlightId }, { toastOnError: true });

      return {
        response,
        nextExtractedFlightId
      };
    },
  });

  const onDelete = async () => {
    const { response, nextExtractedFlightId } = await deleteMutation.mutateAsync();
    if (response.data.hasErrors) return;

    await invalidateFlightFromAiActionQueries(queryClient, extractedFlightId);

    if (nextExtractedFlightId) {
      replaceFlightFromAiSidePanel(nextExtractedFlightId);
      return;
    }

    closePanel();
  };

  return <>
    <ButtonWarning
      label={RESX.Buttons.btnDelete}
      onClick={() => setIsDeleting(true)}
    />
    <DeleteDialog
      spinForever
      isOpen={isDeleting}
      onDelete={onDelete}
      handleClose={() => setIsDeleting(false)}
      message={RESX.GeneralWarnings.DeleteSure}
    />
  </>;
};

export const ApproveButton = ({
  extractedFlightId,
  extractorSessionId,
  replaceFlightFromAiSidePanel
}: FlightFromAiActionButtonProps) => {
  const queryClient = useQueryClient();
  const { closePanel } = useContext(SidePanelActionsContext);

  const approveMutation = useMutation({
    mutationFn: async () => {
      const nextExtractedFlightId = await getNextReadyForApprovalFlightId(extractorSessionId, extractedFlightId);
      const response = await window.CoreApi.Extractor.ApproveExtractedFlight(extractedFlightId, { toastOnError: true });

      return {
        response,
        nextExtractedFlightId
      };
    },
  });

  const onApprove = async () => {
    const { response, nextExtractedFlightId } = await approveMutation.mutateAsync();
    if (response.data.hasErrors) return;

    toastSuccess("Flight has been added");
    await invalidateFlightFromAiActionQueries(queryClient, extractedFlightId);

    if (nextExtractedFlightId) {
      replaceFlightFromAiSidePanel(nextExtractedFlightId);
      return;
    }

    closePanel();
  };

  return <ButtonPrimary
    label={RESX.Buttons.Approve}
    loading={approveMutation.isLoading}
    disabled={approveMutation.isLoading}
    onClick={onApprove}
  />;
};
